import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "./dropdown.module.scss";

export default function SearchAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  fetchOptions,
  getOptionLabel,
  isOptionEqualToValue,
  minSearchLength,
  debounceTime = 700,
  disabled = false,
  error,
  helperText,
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const debounceRef = React.useRef(null);

  const handleSearch = async (searchText) => {
    if (searchText.length < minSearchLength) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchOptions(searchText);
      setOptions(data || []);
    } catch (error) {
      console.error("Autocomplete fetch failed:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      handleSearch(newInputValue);
    }, debounceTime);
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={
        inputValue.length < minSearchLength
          ? `Type at least ${minSearchLength} characters`
          : "No results found"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      className={styles.autocompleteDropdown}
      {...props}
    />
  );
}
